import { useReadQuery, QueryRef } from '@apollo/client/react';
import { SquareCode, FileCode2, Database, ClipboardX, FileQuestion } from 'lucide-react';

import { AssetContentTypeEnum } from 'src/graphql/schema';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from 'src/lib/utils';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { withSuspense } from 'src/hoc';
import { Skeleton } from '@/components/ui/skeleton';

import { useDepositsContext } from '../model';

import type { IAssetsQuery } from 'src/graphql/actions/asset/assets.query';

const AssetCardSkeleton = () => {
  return (
    <Card className="min-h-25 flex-1 flex flex-col justify-between">
      <CardHeader className="flex-row items-center gap-1 space-y-0 pb-2">
        <Skeleton className="size-4 rounded" />
        <Skeleton className="h-4 w-30" />
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1">
          <Skeleton className="h-7 w-12" />
          <Skeleton className="h-3 w-20 mb-0.5" />
        </div>
      </CardContent>
    </Card>
  );
};

type AssetCardProps = {
  title: string;
  icon: React.ElementType;
  contentType: AssetContentTypeEnum;
  queryRef: QueryRef<IAssetsQuery>;
  sectionRef: React.RefObject<HTMLDivElement | null>;
};

const AssetCard = withSuspense(({ title, icon: Icon, contentType, queryRef, sectionRef }: AssetCardProps) => {
  const {
    softwareEscrow: { requiredContentTypes },
  } = useSoftwareEscrow();
  const { data } = useReadQuery(queryRef);

  const assetsCount = data.assets.nodesCount;
  const isMarkedAsRequired = (requiredContentTypes as string[]).includes(contentType);

  const onClick = () => sectionRef.current?.scrollIntoView({ behavior: 'smooth' });

  const MarkedAsNotRequiredContent = () => (
    <div className="flex items-center gap-2">
      <div className="flex items-center justify-center shrink-0 size-4 bg-gray-200 border border-gray-500 rounded-full shadow-sm">
        <ClipboardX className="size-2.5 text-gray-500" />
      </div>
      <div className="text-foreground text-xs">
        Marked as <strong>Not required</strong>
      </div>
    </div>
  );

  const AssetInfoContent = () => (
    <div className="flex items-end gap-1">
      <strong
        className={cn(
          'text-2xl leading-none text-accent-software-escrow',
          !assetsCount && 'text-gray-400 text-xs leading-[1.3]',
        )}
      >
        {String(assetsCount).padStart(2, '0')}
      </strong>
      <span className="text-muted-foreground text-xs">{Boolean(assetsCount) && 'Total '}assets</span>
    </div>
  );

  return (
    <Card
      className={cn(
        'flex-1 min-h-25 flex flex-col justify-between hover:bg-accent cursor-pointer transition-colors',
        !isMarkedAsRequired && 'bg-muted',
      )}
      onClick={onClick}
    >
      <CardHeader className="flex-row items-center gap-1 space-y-0 pb-2">
        <Icon className="size-4" />
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>{isMarkedAsRequired ? <AssetInfoContent /> : <MarkedAsNotRequiredContent />}</CardContent>
    </Card>
  );
}, AssetCardSkeleton);

const UnlabeledAssetCard = withSuspense(({ title, icon: Icon, queryRef, sectionRef }: AssetCardProps) => {
  const { data } = useReadQuery(queryRef);

  const assetsCount = data.assets.nodesCount;

  const onClick = () => sectionRef.current?.scrollIntoView({ behavior: 'smooth' });

  if (!assetsCount) return null;

  return (
    <Card
      className="flex-1 min-h-25 flex flex-col justify-between bg-muted-foreground cursor-pointer"
      onClick={onClick}
    >
      <CardHeader className="flex-row items-center gap-1 space-y-0 pb-2">
        <Icon className="size-4 text-white" />
        <CardTitle className="text-sm text-white font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1">
          <strong className="text-2xl leading-none text-accent-software-backup">
            {String(assetsCount).padStart(2, '0')}
          </strong>
          <span className="text-white text-xs">{Boolean(assetsCount) && 'Total '}assets</span>
        </div>
      </CardContent>
    </Card>
  );
}, AssetCardSkeleton);

type Props = {
  sectionRefs: {
    unlabeled: React.RefObject<HTMLDivElement | null>;
    sourceCode: React.RefObject<HTMLDivElement | null>;
    documentation: React.RefObject<HTMLDivElement | null>;
    data: React.RefObject<HTMLDivElement | null>;
  };
};

const AssetsSummary = ({ sectionRefs }: Props) => {
  const { sourceCodeAssetsQueryRef, documentationAssetsQueryRef, dataAssetsQueryRef, unlabeledAssetsQueryRef } =
    useDepositsContext();

  return (
    <div className="flex flex-col md:flex-row gap-4 mb-6">
      {unlabeledAssetsQueryRef && (
        <UnlabeledAssetCard
          title="Unlabeled"
          icon={FileQuestion}
          queryRef={unlabeledAssetsQueryRef}
          sectionRef={sectionRefs.unlabeled}
        />
      )}
      {sourceCodeAssetsQueryRef && (
        <AssetCard
          title="Source Code"
          icon={SquareCode}
          contentType={AssetContentTypeEnum.SourceCode}
          queryRef={sourceCodeAssetsQueryRef}
          sectionRef={sectionRefs.sourceCode}
        />
      )}
      {documentationAssetsQueryRef && (
        <AssetCard
          title="Documentation"
          icon={FileCode2}
          contentType={AssetContentTypeEnum.Documentation}
          queryRef={documentationAssetsQueryRef}
          sectionRef={sectionRefs.documentation}
        />
      )}
      {dataAssetsQueryRef && (
        <AssetCard
          title="Data"
          icon={Database}
          contentType={AssetContentTypeEnum.Data}
          queryRef={dataAssetsQueryRef}
          sectionRef={sectionRefs.data}
        />
      )}
    </div>
  );
};

export default AssetsSummary;
